import { Button, formatNumber, Icon, IconButton, Typography } from '@smartgift/frontend-toolkit';
import React, { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BulkUploadContactModal } from '@hero/components/organisms/Recipients/BulkUploadContactModal';
import { Table, UserName } from '@hero/molecules';
import { AddRecipientModal, SelectContactModal } from '@hero/organisms';
import { useGetRecognitionRecipientList, useRecogRecipientsDelete } from '@hero/services';
import { useNotification } from '@hero/store/hooks';
import { isEmailValid } from '@hero/utils';

import * as S from './style';

export default function ContactListContent({ updateContact, recognitionCode }) {
  const { t } = useTranslation();
  const { showNotification, showSuccess } = useNotification();

  const [isAddRecipientModalOpen, setIsAddRecipientModalOpen] = useState(false);
  const [isContactUploadModalOpen, setIsContactUploadModalOpen] = useState(false);
  const [isSelectContactModalOpen, setIsSelectContactModalOpen] = useState(false);

  const { mutateAsync: recipientDelete } = useRecogRecipientsDelete();

  const [globalFilters, setGlobalFilters] = useState({});
  const [editRow, setEditRow] = useState(null);

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const { data: recipients } = useGetRecognitionRecipientList(
    {
      code: recognitionCode,
      filters: {
        searchTerm: globalFilters.searchTerm
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      enabled: !!recognitionCode
    }
  );

  const { data: contactList } = useGetRecognitionRecipientList(
    {
      code: recognitionCode,
      options: {
        pagination: false,
        select: 'contact.id'
      }
    },
    {
      enabled: !!recognitionCode
    }
  );

  const onDeleteRecipient = recipient => {
    showNotification({
      title: t('areYouSureRemoveRecipient'),
      message: t('areYouSureRemoveRecipientMessage', { feature: 'oneTimeGift' }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await recipientDelete({
            code: recognitionCode,
            emails: [recipient?.email]
          });

          showSuccess({
            message: (
              <Trans count={1} i18nKey="recipientSuccessfullyRemoved" components={{ b: <b /> }} />
            ),
            type: 'toast'
          });
        }
      }
    });
  };

  const onRemoveAll = () => {
    showNotification({
      title: t('areYouSureRemoveRecipient'),
      message: t('areYouSureRemoveAllRecipientMessage'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemoveAllAndUploadNew'),
        onClick: async () => {
          await recipientDelete({
            code: recognitionCode
          });

          showSuccess({
            message: (
              <Trans
                count={recipients?.totalDocs}
                i18nKey="recipientSuccessfullyRemoved"
                components={{ b: <b /> }}
              />
            ),
            type: 'toast'
          });
        }
      }
    });
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'email',
        header: t('email'),
        size: 120,
        accessorKey: 'email',
        isValidFn: val => val && isEmailValid(val),
        editRow,
        editable: false,
        componentProps: {
          maxLength: 100
        }
      },
      {
        header: t('nameOptional'),
        size: 100,
        accessorKey: 'firstName',
        editRow,
        editable: false,
        componentProps: {
          maxLength: 30
        },
        cell: ({ row }) => {
          return <UserName firstName={row.original.firstName} lastName={row.original.lastName} />;
        }
      },
      {
        id: 'actions',
        header: '',
        size: 5,
        cell: ({ table, row }) => {
          const { updateRecipients } = table.getState();
          const { editMode } = table.options;
          const { editing } = table.getState();
          const rowIsEditing = editing[row.original.id];

          if (editMode === 'row' && rowIsEditing) {
            return (
              <S.ActionButtons>
                <IconButton
                  variant="text"
                  color="success"
                  onClick={() => {
                    setEditRow(null);
                    table.options.meta.handleSave(row);
                  }}
                >
                  <Icon
                    color="var(--color-components-button-secondary-text-fg)"
                    name="success"
                    size="lg"
                  />
                </IconButton>
                <IconButton
                  variant="text"
                  color="error"
                  onClick={() => {
                    setEditRow(null);
                    table.options.meta.handleCancel(row);
                  }}
                >
                  <Icon
                    color="var(--color-components-button-secondary-text-fg)"
                    name="close"
                    size="lg"
                  />
                </IconButton>
              </S.ActionButtons>
            );
          }

          return (
            <S.ActionButtons>
              <IconButton
                variant="text"
                color="error"
                onClick={() => {
                  if (onDeleteRecipient) return onDeleteRecipient(row.original);
                  updateRecipients(recipients => {
                    return recipients.filter(recipient => recipient.id !== row.id);
                  });
                }}
              >
                <Icon name="delete" size="md" />
              </IconButton>
            </S.ActionButtons>
          );
        }
      }
    ],
    [editRow]
  );

  const isEmpty = useMemo(() => !recipients?.docs?.length, [recipients]);

  const selectedContactIds = useMemo(() => {
    return contactList?.docs.map(doc => doc?.contact?.id).filter(id => id) || [];
  }, [contactList]);

  return (
    <S.ContactListContentWrapper>
      <Table
        columns={columns}
        data={recipients?.docs || []}
        state={{
          globalFilter: globalFilters
        }}
        setPagination={setPagination}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        useGlobalFuzzyFilter
        manualPagination
        pagination={{
          pageIndex,
          pageSize,
          total: recipients?.totalDocs
        }}
        pageCount={recipients?.totalPages}
        enabled={{ filter: false, header: false }}
        sectionHeaderProps={{
          enabled: true,
          title: (
            <Typography variant="body2">
              <Trans
                i18nKey="totalContacts"
                values={{ count: formatNumber(recipients?.totalDocs) }}
                components={{ b: <b /> }}
              />
            </Typography>
          ),
          ...(recipients?.totalDocs
            ? {
                cta: (
                  <Button onClick={onRemoveAll} variant="link" color="error">
                    <Icon name="delete" />
                    {t('removeAllAndUploadNew')}
                  </Button>
                )
              }
            : {}),
          searchProps: {
            placeholder: t('searchRecipientsByEmailOrName')
          }
        }}
        components={{
          EmptyTable: () => (
            <S.EmptyContent>
              <Typography variant="body2Italic" emphasis="weak">
                {t('contactListContentEmptyText')}
              </Typography>
            </S.EmptyContent>
          )
        }}
      />
      <S.ActionFooter>
        <Button
          startIcon={<Icon name="add" />}
          variant="outlined"
          size="sm"
          onClick={() => setIsAddRecipientModalOpen(true)}
        >
          {t(isEmpty ? 'addARecipient' : 'addAnotherRecipient')}
        </Button>
        <Button
          startIcon={<Icon name="upload" />}
          variant="outlined"
          size="sm"
          onClick={() => setIsContactUploadModalOpen(true)}
        >
          {t(isEmpty ? 'bulkUpload' : 'addMoreFromFile')}
        </Button>
        <Button
          startIcon={<Icon name="address-book" />}
          variant="link"
          color="secondary"
          size="sm"
          onClick={() => setIsSelectContactModalOpen(true)}
        >
          {t(isEmpty ? 'selectFromContacts' : 'addMoreFromContacts')}
        </Button>
      </S.ActionFooter>
      {isAddRecipientModalOpen && (
        <AddRecipientModal
          isOpen={isAddRecipientModalOpen}
          onRequestClose={() => setIsAddRecipientModalOpen(false)}
          onAsyncSubmit={updateContact}
          recognitionCode={recognitionCode}
        />
      )}
      {isContactUploadModalOpen && (
        <BulkUploadContactModal
          isOpen={isContactUploadModalOpen}
          onRequestClose={() => setIsContactUploadModalOpen(false)}
          onAsyncSubmit={updateContact}
        />
      )}
      {isSelectContactModalOpen && (
        <SelectContactModal
          isOpen={isSelectContactModalOpen}
          onRequestClose={() => setIsSelectContactModalOpen(false)}
          selectedContactIds={selectedContactIds}
          disabledColumns={['birthday', 'anniversary', 'tags']}
          recognitionCode={recognitionCode}
        />
      )}
    </S.ContactListContentWrapper>
  );
}
